import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./AddToListModal.module.css";
import Modal from "components/Modal";
import IconButton from "components/IconButton";
import TextInput from "components/TextInput";
import CategoryList from "components/CategoryList";
import Button from "Shared/Button";
import ListAddModal from "components/ListAddModal";
import FavoritesAddModal from "components/FavoritesAddModal";
import add from "../../assets/add.svg";
import { useTranslation } from "react-i18next";
import { enhancedStockInfo } from "utils/mockStockData";
import {
  useAddListItem,
  useCreateList,
  useListItems,
  useLists,
} from "hooks/useLists";
import { trackProductSavedForLater } from "utils/analytics";

// Sentinel id for the synthetic "Add To Lists" row shown above the system
// lists in MODE 2. Selecting it swaps the visible rows to the user's
// generic lists (Favorites + custom lists) instead of adding to a list.
const ADD_TO_LISTS_ROW_ID = "__add_to_lists__";

function isSavedForLaterCategory(category) {
  return (
    String(category?.name ?? "")
      .trim()
      .toLowerCase() === "saved for later"
  );
}

// System lists (Saved For Later, Mark as Read) stay visible at the top
// level. Everything else - Favorites and any user-created/generic lists -
// is only reachable via the "Add To Lists" row.
function isMainViewCategory(category) {
  const type = String(category?.type ?? "")
    .trim()
    .toUpperCase();
  if (type === "SAVED_FOR_LATER" || type === "BOOKS_READ") return true;

  const name = String(category?.name ?? "")
    .trim()
    .toLowerCase();
  return name === "saved for later" || name === "mark as read";
}

const MARK_AS_READ_LIST = {
  name: "Mark as Read",
  setId: "mark-as-read",
  stocks: [],
  type: 3,
  isNew: true,
  isModal: false,
};

/**
 * AddToListModal has two modes controlled by the `showNewList` prop:
 *
 * MODE 1 — List Management (showNewList: true, default)
 * MODE 2 — Add Item to List (showNewList: false)
 *
 * In MODE 2, this modal keeps its original UI first. When the user clicks
 * Save on a list option row, a second ListAddModal opens for final confirmation.
 *
 * MODE 2 also gates non-system lists behind a synthetic "Add To Lists" row:
 * the top level only shows system lists (Saved For Later, Mark as Read).
 * Selecting "Add To Lists" swaps the view to the user's generic lists
 * (Favorites + custom lists) plus the "New List" creator.
 */
export function AddToListModal({
  onClose,
  onCancel,
  onAdd,
  isOpen,
  item,
  showNewList = true,
  colorOptions: passedColorOptions,
  sizeOptions: passedSizeOptions,
  variantMatrix: passedVariantMatrix,
  // When true (e.g. adding from the cart, where the variant is already
  // selected), skip the ListAddModal/FavoritesAddModal variant-confirmation
  // step entirely and add the item as-is. Re-showing the picker here would
  // let the user silently change the variant, which breaks the backend's
  // matching logic for removing the original cart row.
  skipVariantConfirmation = false,
}) {
  const { t } = useTranslation();
  const [newListName, setNewListName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isListAddModalOpen, setIsListAddModalOpen] = useState(false);
  const [isFavoritesAddModalOpen, setIsFavoritesAddModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // MODE 2 only: whether the "Add To Lists" row has been selected, revealing
  // the generic/custom lists in place of the system lists.
  const [showGenericLists, setShowGenericLists] = useState(false);
  // Only fetch once the modal is actually open; ItemIconSection's own
  // useLists() call keeps the "already read" icon working while closed.
  const { data: endpointLists = [] } = useLists({ enabled: isOpen });
  const addListItem = useAddListItem();
  const selectedListId =
    isListAddModalOpen || isFavoritesAddModalOpen
      ? selectedCategory?.listId ?? selectedCategory?.setId ?? null
      : null;
  const { data: selectedListItems } = useListItems(selectedListId);
  const createList = useCreateList();

  // Reset transient UI state whenever the modal closes so the next open
  // starts back at the top-level view.
  useEffect(() => {
    if (!isOpen) {
      setShowGenericLists(false);
      setIsEditing(false);
      setNewListName("");
    }
  }, [isOpen]);

  const isBook = Boolean(item?.author);

  // Lists this item already belongs to, per the isInList array returned by
  // StockGet/SingleStockGet/ListItemsGet. Used to grey out and check off
  // list rows the item has already been added to.
  const itemListIds = useMemo(() => {
    const ids = new Set();
    (item?.isInList || []).forEach((entry) => {
      const listId = entry?.listId ?? entry?.setId;
      if (listId != null) ids.add(String(listId));
    });
    return ids;
  }, [item?.isInList]);

  // Find the enhanced item from mock data first (needed to determine if item is configurable)
  const enhancedItem = useMemo(
    () =>
      enhancedStockInfo.find(
        (i) => String(i.stockId) === String(item?.stockId)
      ),
    [item?.stockId]
  );

  // Item is configurable if it has itemType from API, OR if enhancedItem has color/size options
  const isConfigurableItem = useMemo(() => {
    const apiConfigurable =
      String(item?.itemType || "")
        .trim()
        .toUpperCase() === "CONFIGURABLE";

    // If API explicitly says it's CONFIGURABLE, use it as configurable
    if (apiConfigurable) return true;

    // If API provides an itemType but it's not CONFIGURABLE, don't use mock data to override
    if (item?.itemType) return false;

    // Only use mock data if API didn't provide itemType
    const enhancedHasVariants =
      enhancedItem &&
      (enhancedItem?.images?.length > 0 ||
        enhancedItem?.colorOptions?.length > 0 ||
        enhancedItem?.detailColors?.length > 0 ||
        enhancedItem?.sizeOptions?.length > 0);

    return enhancedHasVariants;
  }, [item?.itemType, enhancedItem]);

  const defaultCategories = useMemo(
    () => [
      {
        name: t("Favorites"),
        setId: 2,
        stocks: [
          { stockId: 7, order: 1 },
          { stockId: 8, order: 2 },
        ],
        type: 1,
        isNew: true,
        isModal: false,
      },
      {
        name: t("Saved For Later"),
        setId: 3,
        stocks: [
          { stockId: 9, order: 1 },
          { stockId: 10, order: 2 },
        ],
        type: 2,
        isNew: true,
        isModal: false,
      },
      {
        name: t("Mark as Read"),
        setId: 4,
        stocks: [
          { stockId: 9, order: 1 },
          { stockId: 10, order: 2 },
        ],
        type: 2,
        isNew: true,
        isModal: false,
      },
    ],
    [t]
  );

  const endpointCategories = useMemo(
    () =>
      (endpointLists || [])
        .filter((list) => {
          const listType = String(list?.type ?? "").toUpperCase();
          // Purchased Items is a system-generated list built from completed
          // transactions; users can never manually add to it.
          if (listType === "PURCHASED_ITEMS") {
            return false;
          }
          // Only show BOOKS_READ list for book items
          if (listType === "BOOKS_READ" && !isBook) {
            return false;
          }
          return true;
        })
        .map((list) => {
          const listId = list?.listId ?? list?.setId;
          const listType = String(list?.type ?? "").toUpperCase();
          // Display "Mark as Read" for BOOKS_READ type lists in the modal
          const displayName =
            listType === "BOOKS_READ" ? "Mark as Read" : list?.name;
          return {
            ...list,
            name: displayName,
            listId,
            setId: listId,
            stocks: list?.stocks ?? [],
            type: list?.type ?? "GENERIC",
            // Keep the existing modal row behavior that uses the ADD icon.
            isNew: true,
            isModal: false,
            isSystemList: listType === "BOOKS_READ" ? true : false,
          };
        }),
    [endpointLists, isBook]
  );

  const fallbackCategories = useMemo(
    () =>
      isBook ? [...defaultCategories, MARK_AS_READ_LIST] : defaultCategories,
    [isBook, defaultCategories]
  );

  // isInList only says which lists hold the product, not which variants, so
  // variant items stay selectable and the picker flags exact duplicates.
  const allowsVariantSelection =
    !skipVariantConfirmation &&
    (isConfigurableItem ||
      passedColorOptions?.length > 0 ||
      passedSizeOptions?.length > 0);

  const categories = useMemo(() => {
    const base =
      endpointCategories.length > 0 ? endpointCategories : fallbackCategories;

    return base.map((category) => {
      const listId = category?.listId ?? category?.setId;
      return {
        ...category,
        isAlreadyInList:
          !allowsVariantSelection &&
          listId != null &&
          itemListIds.has(String(listId)),
      };
    });
  }, [
    endpointCategories,
    fallbackCategories,
    itemListIds,
    allowsVariantSelection,
  ]);

  // MODE 2 only: split rows into the top-level system lists and the
  // generic/custom lists that live behind the "Add To Lists" row.
  const { mainCategories, genericCategories } = useMemo(() => {
    const main = [];
    const generic = [];
    categories.forEach((category) => {
      if (isMainViewCategory(category)) {
        main.push(category);
      } else {
        generic.push(category);
      }
    });
    return { mainCategories: main, genericCategories: generic };
  }, [categories]);

  const addToListsRow = useMemo(
    () => ({
      name: t("addToListModal.addToLists", "Add To Lists"),
      listId: ADD_TO_LISTS_ROW_ID,
      setId: ADD_TO_LISTS_ROW_ID,
      stocks: [],
      type: "ADD_TO_LISTS",
      isNew: true,
      isModal: false,
      isSystemList: true,
      isAlreadyInList: false,
    }),
    [t]
  );

  const colorOptions = useMemo(() => {
    // Prefer passed color options (from API variants)
    if (passedColorOptions && passedColorOptions.length > 0) {
      return passedColorOptions;
    }

    if (!isConfigurableItem) {
      return [];
    }

    if (enhancedItem?.detailColors?.length) {
      return enhancedItem.detailColors.map((option) => {
        if (typeof option === "string") {
          return { label: option, imageUrl: enhancedItem?.imageUrl };
        }

        return {
          label: option?.label || option?.value || "",
          imageUrl: option?.imageUrl || enhancedItem?.imageUrl,
        };
      });
    }

    if (enhancedItem?.colorOptions?.length) {
      return enhancedItem.colorOptions.map((option, index) => {
        if (typeof option === "string") {
          return {
            label: option,
            imageUrl:
              enhancedItem?.images?.[index]?.src || enhancedItem?.imageUrl,
          };
        }

        return {
          label: option?.label || option?.value || "",
          imageUrl:
            option?.imageUrl ||
            enhancedItem?.images?.[index]?.src ||
            enhancedItem?.imageUrl,
        };
      });
    }

    if (enhancedItem?.images?.length) {
      return enhancedItem.images.map((option, index) => ({
        label: option?.label || option?.name || `Option ${index + 1}`,
        imageUrl: option?.src || enhancedItem?.imageUrl,
      }));
    }

    return [];
  }, [enhancedItem, isConfigurableItem, passedColorOptions]);

  const sizeOptions = useMemo(() => {
    // Prefer passed size options (from API variants)
    if (passedSizeOptions && passedSizeOptions.length > 0) {
      return passedSizeOptions;
    }

    if (!isConfigurableItem) {
      return [];
    }

    return (enhancedItem?.sizeOptions || []).map((option) => {
      if (typeof option === "string") {
        return { label: option, isSoldOut: false };
      }

      return {
        label: option?.label || option?.value || "",
        isSoldOut: Boolean(option?.isSoldOut),
      };
    });
  }, [enhancedItem, isConfigurableItem, passedSizeOptions]);

  const hasVariantChoices = colorOptions.length > 0 || sizeOptions.length > 0;

  const listAddItem = useMemo(
    () => ({
      ...item,
      // Preserve the item's already-selected color/size (e.g. from a cart
      // row) rather than defaulting to the first available option. Only
      // fall back to the first option when no selection was made.
      color:
        item?.color !== undefined
          ? item.color
          : hasVariantChoices && colorOptions.length > 0
          ? colorOptions[0].label
          : undefined,
      size:
        item?.size !== undefined
          ? item.size
          : hasVariantChoices && sizeOptions.length > 0
          ? sizeOptions[0].label
          : undefined,
      imageUrl:
        item?.imageUrl ||
        colorOptions[0]?.imageUrl ||
        enhancedItem?.imageUrl ||
        item?.image,
      variantMatrix: passedVariantMatrix || undefined,
    }),
    [
      item,
      colorOptions,
      sizeOptions,
      enhancedItem,
      hasVariantChoices,
      passedVariantMatrix,
    ]
  );

  const handleSaveClick = () => {
    const name = newListName.trim();
    if (name === "") return;

    // Create the list via the API; the lists query gets invalidated on
    // settle, so the generic list view re-renders with the new list once
    // it comes back from the refetch - no local/optimistic list needed.
    // listTypeId hardcoded to match MyListPage until BSM fixes the
    // uniqueness constraint to allow multiple lists of the same type.
    createList.mutate(
      { name, listTypeId: 4 },
      {
        onSuccess: () => {
          setNewListName("");
          setIsEditing(false);
        },
      }
    );
  };

  // Selecting the synthetic "Add To Lists" row swaps the view to the
  // generic/custom lists instead of adding the item to anything.
  const handleMainRowSave = (category) => {
    if (category?.listId === ADD_TO_LISTS_ROW_ID) {
      setShowGenericLists(true);
      return;
    }
    handleListSave(category);
  };

  const handleCancelClick = () => {
    setNewListName("");
    setIsEditing(false);
    onCancel?.();
  };

  const handleTitleClick = () => {
    setIsEditing(true);
  };

  const handleListSave = (category) => {
    if (showNewList) return;
    setSelectedCategory(category);

    const listType = String(category?.type ?? "").toUpperCase();

    if (skipVariantConfirmation) {
      // Variant already known (e.g. from the cart) - add directly without
      // showing the reselection picker.
      if (listType === "USER_FAVORITE") {
        handleConfirmAddToFavorites(listAddItem, category);
      } else {
        handleConfirmAddToList(listAddItem, category);
      }
      return;
    }

    if (listType === "USER_FAVORITE") {
      setIsFavoritesAddModalOpen(true);
    } else {
      setIsListAddModalOpen(true);
    }
  };

  const handleConfirmAddToList = (payload, categoryOverride) => {
    const category = categoryOverride ?? selectedCategory;
    if (!category) return;

    const listId = category?.listId ?? category?.setId;
    const stockId = payload?.stockId ?? item?.stockId;

    if (listId != null && stockId != null) {
      addListItem.mutate(
        {
          listId,
          stockId,
          variationId: payload?.variationId ?? item?.variationId ?? null,
          variantId: payload?.variantId ?? item?.variantId ?? null,
          sortOrder: payload?.sortOrder ?? null,
          title: payload?.title ?? item?.title,
          memberPrice: payload?.memberPrice ?? item?.memberPrice,
          retailPrice: payload?.retailPrice ?? item?.retailPrice,
          imageUrl: payload?.imageUrl ?? item?.imageUrl,
          image: payload?.image ?? item?.image,
          itemType: payload?.itemType ?? item?.itemType,
          author: payload?.author ?? item?.author,
          color: payload?.color ?? item?.color,
          size: payload?.size ?? item?.size,
        },
        {
          onSuccess: () => {
            if (isSavedForLaterCategory(category)) {
              trackProductSavedForLater({ ...item, ...payload, stockId });
            }
            onAdd?.({
              category,
              item: payload,
            });
          },
        }
      );
    }

    setIsListAddModalOpen(false);
    onClose?.();
  };

  const handleConfirmAddToFavorites = (payload, categoryOverride) => {
    const category = categoryOverride ?? selectedCategory;
    if (!category) return;

    const listId = category?.listId ?? category?.setId;
    const stockId = payload?.stockId ?? item?.stockId;

    if (listId != null && stockId != null) {
      addListItem.mutate(
        {
          listId,
          stockId,
          variationId: payload?.variationId ?? item?.variationId ?? null,
          variantId: payload?.variantId ?? item?.variantId ?? null,
          sortOrder: payload?.sortOrder ?? null,
          title: payload?.title ?? item?.title,
          memberPrice: payload?.memberPrice ?? item?.memberPrice,
          retailPrice: payload?.retailPrice ?? item?.retailPrice,
          imageUrl: payload?.imageUrl ?? item?.imageUrl,
          image: payload?.image ?? item?.image,
          itemType: payload?.itemType ?? item?.itemType,
          author: payload?.author ?? item?.author,
          color: payload?.color ?? item?.color,
          size: payload?.size ?? item?.size,
        },
        {
          onSuccess: () => {
            onAdd?.({
              category,
              item: payload,
            });
          },
        }
      );
    }

    setIsFavoritesAddModalOpen(false);
    onClose?.();
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !isListAddModalOpen && !isFavoritesAddModalOpen}
        className={styles.modal}
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className={styles.container}>
          {(showNewList || showGenericLists) &&
            (isEditing ? (
              <div className={styles.textInputContainer}>
                <TextInput
                  label={t("New List")}
                  placeholder={t("Create New List")}
                  value={newListName}
                  setValue={(e) => setNewListName(e.target.value)}
                />
                <div className={styles.buttonsContainer}>
                  <Button
                    text={t("Cancel")}
                    onClick={handleCancelClick}
                    className="cancelBtn"
                  />
                  <Button
                    text={t("Save")}
                    onClick={handleSaveClick}
                    className="saveBtn"
                  />
                </div>
              </div>
            ) : (
              <div className={styles.title} onClick={handleTitleClick}>
                <IconButton icon={add} /> {t("New List")}
              </div>
            ))}

          <div className={styles.list}>
            {showNewList ? (
              <CategoryList
                categories={categories}
                onAdd={onAdd}
                onSave={handleListSave}
                isInModal={true}
              />
            ) : showGenericLists ? (
              <CategoryList
                categories={genericCategories}
                onAdd={onAdd}
                onSave={handleListSave}
                isInModal={true}
              />
            ) : (
              <CategoryList
                categories={[addToListsRow, ...mainCategories]}
                onAdd={onAdd}
                onSave={handleMainRowSave}
                isInModal={true}
              />
            )}
          </div>
          <Button text={t("Close")} className="closeBtn" onClick={onClose} />
        </div>
      </Modal>

      <ListAddModal
        isOpen={isListAddModalOpen}
        onClose={() => setIsListAddModalOpen(false)}
        onCancel={() => setIsListAddModalOpen(false)}
        onSave={handleConfirmAddToList}
        item={listAddItem}
        existingItems={selectedListItems}
        colorOptions={colorOptions.length > 0 ? colorOptions : undefined}
        sizeOptions={sizeOptions.length > 0 ? sizeOptions : undefined}
      />

      <FavoritesAddModal
        isOpen={isFavoritesAddModalOpen}
        onClose={() => setIsFavoritesAddModalOpen(false)}
        onCancel={() => setIsFavoritesAddModalOpen(false)}
        onSave={handleConfirmAddToFavorites}
        item={listAddItem}
        existingItems={selectedListItems}
        colorOptions={colorOptions.length > 0 ? colorOptions : undefined}
        sizeOptions={sizeOptions.length > 0 ? sizeOptions : undefined}
      />
    </>
  );
}

AddToListModal.propTypes = {
  name: PropTypes.string,
  onClose: PropTypes.func,
  onAdd: PropTypes.func,
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  showNewList: PropTypes.bool,
  colorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ),
  sizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      isSoldOut: PropTypes.bool,
    })
  ),
  variantMatrix: PropTypes.object,
  skipVariantConfirmation: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    color: PropTypes.string,
    image: PropTypes.any,
    isInList: PropTypes.arrayOf(
      PropTypes.shape({
        listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        code: PropTypes.string,
      })
    ),
  }),
};

export default AddToListModal;
